import formatDate from 'date-fns/format'

import {
  uploadCreativeFile,
  createCampaign,
  getCampaign,
  updateCampaign,
  getAdvertiser,
} from '@/api/ads'

const state = {
  adsPlacementsData: [],
  budgetingData: {},
  isFetchingPlacement: false,
  lastError: undefined,
  detail: {},
  isFetchingDetail: false,
  targeting: {},
  isFetchingAdvertiser: false,
  advertiser: {},
  paymentData: null,
}

const mutations = {
  SET_ADS_PLACEMENTS_DATA(state, { data }) {
    state.adsPlacementsData = data
  },
  SET_BUDGETING_DATA(state, { data }) {
    state.budgetingData = data
  },
  SET_IS_FETCHING(state, { name, value }) {
    state[name] = value
  },
  SET_ERROR(state, error) {
    state.lastError = error
  },
  SET_CAMPAIGN_DETAIL(state, { data }) {
    state.detail = data
  },
  SET_TARGETING(state, { data }) {
    state.targeting = data
  },
  SET_ADVERTISER(state, { data }) {
    state.advertiser = data
  },
  SET_PAYMENT_DATA(state, data) {
    state.paymentData = data
  },
}

const getters = {
  adsPlacementsData: (state) => state.adsPlacementsData,
  isFetchingPlacement: (state) => state.isFetchingPlacement,
  budgetingData: (state) => state.budgetingData,
  lastError: (state) => state.lastError,
  detail: (state) => state.detail,
  isFetchingDetail: (state) => state.isFetchingDetail,
  targeting: (state) => state.targeting,
  advertiser: (state) => state.advertiser,
  isFetchingAdvertiser: (state) => state.isFetchingAdvertiser,
  paymentData: (state) => state.paymentData,
}

const actions = {
  async createAdsPlacements({ commit }, { data }) {
    try {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingPlacement',
        value: true,
      })
      const res = await uploadCreativeFile(data)
      return res?.data?.data
    } catch (error) {
      commit('SET_ERROR', error?.response?.data)
      throw error
    } finally {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingPlacement',
        value: false,
      })
    }
  },
  setBudgetingData({ commit }, { data }) {
    commit('SET_BUDGETING_DATA', { data })
  },
  setPaymentData({ commit }, data) {
    commit('SET_PAYMENT_DATA', data)
  },
  async submitCampaign(
    { commit, state },
    { campaignName, placementsData, budgetingData }
  ) {
    const data = {
      name: campaignName,
      ...budgetingData,
      placements: placementsData.map((el) => ({
        creative_id: el.id,
        targeting: {
          languages: [el.language],
        },
        creative_metadata: {
          advertiser_internal_id: el.metadata.advertiserInternalId,
          merchant_id: el.metadata.merchantId,
          store_logo: el.metadata.storeLogo,
          store_name: el.metadata.storeName,
          language: el.metadata.language,
        },
        placement_id: el.placementId,
      })),
      targeting: {
        ...state.targeting,
      },
    }

    if (budgetingData.start_date) {
      const startDate = formatDate(
        new Date(budgetingData.start_date),
        'yyyy-MM-dd'
      )
      data.start_date = `${startDate}T00:00:00Z`
    }

    if (budgetingData.end_date) {
      const endDate = formatDate(new Date(budgetingData.end_date), 'yyyy-MM-dd')
      data.end_date = `${endDate}T23:59:59Z`
    }

    const res = await createCampaign(data)
    return res
  },
  async fetchCampaignDetail({ commit }, { campaignId }) {
    try {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingDetail',
        value: true,
      })
      const res = await getCampaign(campaignId)
      if (res?.data?.data) {
        commit('SET_CAMPAIGN_DETAIL', { data: res?.data?.data })
      }
    } finally {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingDetail',
        value: false,
      })
    }
  },
  setTargeting({ commit }, { data }) {
    commit('SET_TARGETING', { data })
  },
  async updateCampaign(
    { state, dispatch },
    {
      targetAudiencesData,
      placementsData,
      budgetingData,
      campaignId,
      campaignName,
    }
  ) {
    const originalPlacements = state?.detail?.placements || []
    const placements = placementsData.map((el) => {
      const indexSelected = originalPlacements.findIndex(
        (item) =>
          item.placementId === el.placementId &&
          item.targeting.languages.includes(el.language)
      )

      const item = {
        creative_id: el.id,
        creative_metadata: {
          advertiser_internal_id: el.metadata.advertiserInternalId,
          merchant_id: el.metadata.merchantId,
          store_logo: el.metadata.storeLogo,
          store_name: el.metadata.storeName,
          language: el.metadata.language,
        },
        placement_id: el.placementId,
        title: el.title,
        click_url: el.clickUrl,
        image_url: el.imageLink,
        targeting: {
          languages: [el.language],
        },
      }
      if (indexSelected > -1) {
        item.external_id = originalPlacements[indexSelected].externalId
      }

      return item
    })

    const data = {
      id: state.detail.id,
      external_advertiser_id: state.detail.externalAdvertiserId,
      external_id: state.detail.externalId,
      review_status: state.detail.reviewStatus,
      review_message: state.detail.reviewMessage,
      risk_level: state.detail.riskLevel,
      status: state.detail.status,
      ...budgetingData,
      is_active: state.detail.isActive,
      created_at: state.detail.createdAt,
      updated_at: state.detail.updatedAt,
      placements,
      targeting: targetAudiencesData,
      name: campaignName,
    }

    if (budgetingData.start_date) {
      const startDate = formatDate(
        new Date(budgetingData.start_date),
        'yyyy-MM-dd'
      )
      data.start_date = `${startDate}T00:00:00Z`
    }

    if (budgetingData.end_date) {
      const endDate = formatDate(new Date(budgetingData.end_date), 'yyyy-MM-dd')
      data.end_date = `${endDate}T23:59:59Z`
    }

    const res = await updateCampaign({
      campaignId,
      data,
    })
    return res
  },
  async fetchAdvertiser({ commit }) {
    try {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingAdvertiser',
        value: true,
      })
      const res = await getAdvertiser()
      if (res?.data?.data) {
        commit('SET_ADVERTISER', { data: res?.data?.data })
      }
    } finally {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingAdvertiser',
        value: false,
      })
    }
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
